import React from "react";
import { AUTOPAY_METHOD_COPY } from "../../utils/autopay";

export const EMPTY_BANK = {
  beneficiary_name: "",
  account_number: "",
  confirm_account_number: "",
  ifsc_code: "",
  account_type: "savings",
};

export const DEFAULT_AUTOPAY_VALUE = {
  mode: "once", // "once" | "autopay"
  method: null, // "upi" | "card" | "emandate"
  authType: "netbanking", // e-mandate only: "netbanking" | "debitcard"
  bank: EMPTY_BANK,
};

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

/** Returns a user-facing error string, or null when the selection is complete. */
export function validateAutopaySelection(value, options) {
  if (value.mode !== "autopay") return null;
  const method = (options?.methods || []).find((m) => m.method === value.method);
  if (!method) return "Choose an AutoPay method.";
  if (!method.eligible) return method.reason || "This method can't cover your renewal.";
  if (value.method === "emandate") {
    const b = value.bank || {};
    const name = (b.beneficiary_name || "").trim();
    const acct = (b.account_number || "").replace(/\s/g, "");
    if (name.length < 4) return "Enter the account holder name as printed on your passbook.";
    if (!/^\d{6,18}$/.test(acct)) return "Enter a valid bank account number.";
    if (acct !== (b.confirm_account_number || "").replace(/\s/g, ""))
      return "Account numbers don't match.";
    if (!IFSC_RE.test((b.ifsc_code || "").trim().toUpperCase()))
      return "Enter a valid 11-character IFSC code.";
  }
  return null;
}

export function bankPayload(bank) {
  return {
    beneficiary_name: (bank.beneficiary_name || "").trim(),
    account_number: (bank.account_number || "").replace(/\s/g, ""),
    ifsc_code: (bank.ifsc_code || "").trim().toUpperCase(),
    account_type: bank.account_type || "savings",
  };
}

const rupees = (n) =>
  Number.isFinite(Number(n)) ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

/**
 * AutoPay picker.
 *  - showModeToggle: checkout shows "Pay once" vs "Pay & turn on AutoPay";
 *    Settings hides it (always AutoPay).
 */
const AutopayOptions = ({
  options,
  loading,
  error,
  value,
  onChange,
  disabled = false,
  showModeToggle = true,
  darkMode = false,
}) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const setBank = (patch) => set({ bank: { ...value.bank, ...patch } });

  const muted = darkMode ? "text-slate-400" : "text-gray-500";
  const border = darkMode ? "border-slate-600" : "border-gray-200";
  const inputCls = `w-full rounded-lg border ${border} px-3 py-2 text-sm ${
    darkMode ? "bg-slate-800 text-white" : "bg-white"
  } focus:outline-none focus:ring-2 focus:ring-[#5E5ADB]`;

  if (loading) {
    return <p className={`text-sm ${muted}`}>Checking AutoPay options…</p>;
  }
  if (error) {
    return <p className="text-sm text-amber-700">{error}</p>;
  }
  if (!options?.enabled || !(options.methods || []).length) return null;

  const current = options.current || {};
  const autopayOn = value.mode === "autopay";
  const eligibleMethods = options.methods.filter((m) => m.eligible);

  return (
    <fieldset disabled={disabled} className="space-y-3">
      {showModeToggle && (
        <>
          <legend className="text-sm font-medium mb-1">How do you want to pay?</legend>

          {current.status === "active" && current.coversRenewal && (
            <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
              AutoPay is already on ({current.label}). Future renewals will be debited
              automatically. Choose AutoPay below only to switch the method.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
            {[
              { id: "once", title: "Pay once", sub: "Renew manually next time" },
              {
                id: "autopay",
                title: "Pay & turn on AutoPay",
                sub: `Auto-renews at ${rupees(options.renewalAmountRupees)} / ${
                  options.billingCycle === "yearly" ? "year" : "30 days"
                }`,
              },
            ].map((opt) => (
              <button
                type="button"
                key={opt.id}
                role="radio"
                aria-checked={value.mode === opt.id}
                onClick={() =>
                  set({
                    mode: opt.id,
                    method:
                      opt.id === "autopay" && !value.method
                        ? eligibleMethods[0]?.method || null
                        : value.method,
                  })
                }
                className={`text-left rounded-xl border px-3 py-2 transition ${
                  value.mode === opt.id
                    ? "border-[#5E5ADB] ring-2 ring-[#5E5ADB]/30"
                    : border
                }`}
              >
                <span className="block text-sm font-medium">{opt.title}</span>
                <span className={`block text-xs ${muted}`}>{opt.sub}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {autopayOn && (
        <div className="space-y-2">
          <p className={`text-xs ${muted}`}>
            {showModeToggle
              ? "You'll pay today's amount first, then approve AutoPay in a second step."
              : `Renewal amount: ${rupees(options.renewalAmountRupees)}.`}{" "}
            Your bank or UPI app notifies you 24 hours before every debit. Cancel any time
            in Settings.
          </p>

          {options.methods.map((m) => {
            const copy = AUTOPAY_METHOD_COPY[m.method] || {};
            const selected = value.method === m.method;
            return (
              <label
                key={m.method}
                className={`flex gap-3 rounded-xl border px-3 py-3 ${
                  m.eligible ? "cursor-pointer" : "opacity-60 cursor-not-allowed"
                } ${selected ? "border-[#5E5ADB] ring-2 ring-[#5E5ADB]/30" : border}`}
              >
                <input
                  type="radio"
                  name="autopay-method"
                  className="mt-1 accent-[#5E5ADB]"
                  checked={selected}
                  disabled={!m.eligible}
                  onChange={() => set({ method: m.method })}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium">{copy.title || m.label}</span>
                  <span className={`block text-xs ${muted}`}>{copy.subtitle}</span>
                  {m.eligible ? (
                    <span className={`block text-xs mt-1 ${muted}`}>
                      {copy.auth} Limit per debit: {rupees(m.maxAmountRupees)}.
                    </span>
                  ) : (
                    <span className="block text-xs mt-1 text-amber-700">{m.reason}</span>
                  )}
                </span>
              </label>
            );
          })}

          {value.method === "emandate" && (
            <div className={`rounded-xl border ${border} p-3 space-y-2`}>
              <div className="flex gap-2" role="radiogroup" aria-label="Authorise using">
                {[
                  { id: "netbanking", label: "Net banking" },
                  { id: "debitcard", label: "Debit card" },
                ].map((a) => (
                  <button
                    type="button"
                    key={a.id}
                    role="radio"
                    aria-checked={value.authType === a.id}
                    onClick={() => set({ authType: a.id })}
                    className={`flex-1 text-sm rounded-lg border px-3 py-2 ${
                      value.authType === a.id
                        ? "border-[#5E5ADB] bg-[#5E5ADB]/10 font-medium"
                        : border
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>

              <input
                className={inputCls}
                placeholder="Account holder name (as on passbook)"
                autoComplete="name"
                value={value.bank.beneficiary_name}
                onChange={(e) => setBank({ beneficiary_name: e.target.value })}
              />
              <input
                className={inputCls}
                placeholder="Account number"
                inputMode="numeric"
                autoComplete="off"
                value={value.bank.account_number}
                onChange={(e) => setBank({ account_number: e.target.value.replace(/[^\d\s]/g, "") })}
              />
              <input
                className={inputCls}
                placeholder="Confirm account number"
                inputMode="numeric"
                autoComplete="off"
                onPaste={(e) => e.preventDefault()}
                value={value.bank.confirm_account_number}
                onChange={(e) =>
                  setBank({ confirm_account_number: e.target.value.replace(/[^\d\s]/g, "") })
                }
              />
              <div className="flex gap-2">
                <input
                  className={inputCls}
                  placeholder="IFSC (e.g. HDFC0001234)"
                  maxLength={11}
                  autoCapitalize="characters"
                  value={value.bank.ifsc_code}
                  onChange={(e) => setBank({ ifsc_code: e.target.value.toUpperCase() })}
                />
                <select
                  className={`${inputCls} max-w-[9rem]`}
                  value={value.bank.account_type}
                  onChange={(e) => setBank({ account_type: e.target.value })}
                >
                  <option value="savings">Savings</option>
                  <option value="current">Current</option>
                </select>
              </div>
              <p className={`text-xs ${muted}`}>
                Use the account your company pays from. We store only the last 4 digits.
              </p>
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
};

export default AutopayOptions;
